<script lang="ts">
	import { currentMarket, userBalancesEnriched } from '$lib/home/store';
	import BigNumber from 'bignumber.js';
	import { input, longOrShort } from '../store';
	import { addDollarSign, getNumBackInUnits, getPrecisionOfMarket } from '$lib/util/util';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';

	export let sectionExpanded: boolean = true;

	let currentSkew: BigNumber,
		priceImpact: BigNumber,
		initialSkew: BigNumber,
		finalSkew: BigNumber,
		executionPrice: BigNumber,
		totalPrice: BigNumber;

	$: if ($currentMarket && $userBalancesEnriched) {
		let amount: string = new BigNumber($input)
			.dividedBy(new BigNumber($currentMarket.currentMarketPrice))
			.multipliedBy(1e18)
			.toFixed(0);
		if ($longOrShort == 'Short') amount = '-' + amount;

		// currentSkew = ethMarket.longOI - ethMarket.shortOI
		currentSkew = new BigNumber($currentMarket?.longOI).minus($currentMarket?.shortOI);

		// pdBefore = currentSkew / ethMarket.skewScale
		initialSkew = currentSkew.div($currentMarket.skewScale);

		// pdAfter  = (currentSkew + amount) / ethMarket.skewScale
		finalSkew = currentSkew.plus(amount).div($currentMarket.skewScale);

		// fillPrice = pythPrice x (2+pdBefore+pdAfter)/2
		executionPrice = new BigNumber($currentMarket.currentOraclePrice)
			.multipliedBy(new BigNumber(2).plus(initialSkew.plus(finalSkew)))
			.div(new BigNumber(2))
			.div(1e18);

		// priceImpact% = abs(1-fillPrice/pythPrice)
		priceImpact = new BigNumber(1).minus(executionPrice.div($currentMarket.currentOraclePrice));

		totalPrice = new BigNumber(amount).multipliedBy(executionPrice).dividedBy(1e18);
	}
</script>

<Tooltip
	header="Price of current trade size"
	title="This is the price of the trade size input, it can be different than apparent price of the trade due to Discount/Premium mechanism based on skew of the market. <br/>
	<br/>
	<b>Formula for calculation</b>: <br/>
	Total Price = Execution Price * Trade Size <br/>	
	"
>
	<div class="flex w-full justify-between">
		<button class="text-fx-grey-400 text-xs cursor-pointer"> Cost of Perp. Size </button>
		<div class="text-fx-grey-300 text-xs font-medium">
			{addDollarSign(totalPrice.abs().toFixed())}
		</div>
	</div>
</Tooltip>

<Tooltip
	header="Expected Price"
	title="Due to the Discount/Premium mechanism by synthetix, trades which contribute to a skew are given a Premium Price and trades which reduce the skew are offered a Discounted Price. <br/>
	<br/>
	<b>Formula for calculation</b>: <br/>
	Expected Price = Current Oracle Price * (1 - 0.5 * (Initial Skew + Final Skew)) <br/>
	"
>
	<div class="flex w-full justify-between" class:hidden={!sectionExpanded}>
		<div class="text-fx-grey-400 text-xs">Execution Price</div>
		<div class="text-fx-grey-300 text-xs font-medium">
			{addDollarSign(executionPrice.toFixed(), getPrecisionOfMarket($currentMarket?.asset))}
		</div>
	</div>
</Tooltip>

<Tooltip
	header="Price Impact"
	title="Calculated on the impact your trade makes on the skew
	<br/> <br/>
	<b>Formula for calculation</b>: <br/>
	Price Impact = (Final Skew - Initial Skew) * 100 / 2 <br/>
	"
>
	<div class="flex w-full justify-between" class:hidden={!sectionExpanded}>
		<div class="text-fx-grey-400 text-xs">Price Impact</div>
		<div class="text-fx-grey-300 text-xs font-medium">
			{priceImpact.toFixed(2)}%
		</div>
	</div>
</Tooltip>
